import * as React from 'react';
import { Box, Button, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import CampaignBanner from './CampaignBanner';

const CheckIcon = ({ size = 16, color = '#0091A5' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="5 12 10 17 19 7" />
    </svg>
);

const HelpIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const PricingPlanSlice = ({ data }) => {
    const {
        colorTheme = 'off-white',
        eyebrow = '',
        title: sliceTitle = '',
        subtitle = '',
        helpTitle = '',
        helpBody = '',
        items = [],
        topBanner = null,
        currentCurrency = '',
        currencies = []
    } = data || {};

    const theme = generateTheme(colorTheme);

    const [currencyMenuOpen, setCurrencyMenuOpen] = React.useState(false);
    const currencyMenuRef = React.useRef(null);

    React.useEffect(() => {
        if (!currencyMenuOpen) return;
        const handleDocClick = (e) => {
            if (currencyMenuRef.current && !currencyMenuRef.current.contains(e.target)) {
                setCurrencyMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleDocClick);
        return () => document.removeEventListener('mousedown', handleDocClick);
    }, [currencyMenuOpen]);

    const handleCurrencyChange = (cur) => {
        document.cookie = `cc_currency=${cur};path=/;max-age=${365 * 24 * 3600};SameSite=Lax`;
        try { localStorage.setItem('cc_currency', cur); } catch (e) { /* storage disabled — cookie still set */ }
        window.location.reload();
    };

    const variantStyles = (variant) => {
        switch (variant) {
            case 'featured-dark':
                return {
                    background: '#16121C',
                    border: 'none',
                    showStripe: true,
                    shadow: '0 6px 32px rgba(22, 18, 28, 0.15)',
                    tagColor: '#C4B5FD',
                    titleColor: '#D8D5D0',
                    taglineColor: '#B8B5B0',
                    dividerColor: 'rgba(255,255,255,0.10)',
                    currencyColor: '#C4B5FD',
                    detailColor: '#B8B5B0',
                    checkColor: '#84C5CB',
                    featureTextColor: '#B8B5B0',
                    featureBoldColor: '#fff',
                    defaultButton: 'filled'
                };
            case 'featured-light':
                return {
                    background: '#fff',
                    border: 'none',
                    showStripe: true,
                    shadow: '0 6px 32px rgba(22, 18, 28, 0.10)',
                    tagColor: '#5C33D7',
                    titleColor: '#2D2A26',
                    taglineColor: '#4A4744',
                    dividerColor: '#E8E4DF',
                    currencyColor: '#5C33D7',
                    detailColor: '#78746E',
                    checkColor: '#0091A5',
                    featureTextColor: '#4A4744',
                    featureBoldColor: '#16121C',
                    defaultButton: 'filled'
                };
            case 'grey':
                return {
                    background: '#EDE9E4',
                    border: 'none',
                    showStripe: false,
                    shadow: 'none',
                    tagColor: '#78746E',
                    titleColor: '#2D2A26',
                    taglineColor: '#4A4744',
                    dividerColor: '#DDD7CE',
                    currencyColor: '#78746E',
                    detailColor: '#78746E',
                    checkColor: '#0091A5',
                    featureTextColor: '#4A4744',
                    featureBoldColor: '#16121C',
                    defaultButton: 'outlined-contrast'
                };
            default:
                return {
                    background: '#fff',
                    border: 'none',
                    showStripe: false,
                    shadow: 'none',
                    tagColor: '#78746E',
                    titleColor: '#2D2A26',
                    taglineColor: '#4A4744',
                    dividerColor: '#E8E4DF',
                    currencyColor: '#78746E',
                    detailColor: '#78746E',
                    checkColor: '#0091A5',
                    featureTextColor: '#4A4744',
                    featureBoldColor: '#16121C',
                    defaultButton: 'filled-contrast'
                };
        }
    };

    const planFor = (item, idx) => {
        const variant = item.variant || (item.featured ? 'featured-dark' : 'default');
        const isFeatured = variant.startsWith('featured');
        const s = variantStyles(variant);

        const cardSx = {
            position: 'relative',
            borderRadius: '16px',
            p: { xs: '28px 24px', md: '36px 32px' },
            display: 'flex',
            flexDirection: 'column',
            color: variant === 'featured-dark' ? '#fff' : '#16121C',
            ...(s.shadow !== 'none' ? { boxShadow: s.shadow } : {}),
            ...(s.showStripe ? {
                background: `linear-gradient(${s.background}, ${s.background}) padding-box, linear-gradient(135deg, #84C5CB 0%, #0091A5 50%, #5C33D7 100%) border-box`,
                border: '8px solid transparent'
            } : {
                background: s.background,
                border: s.border
            })
        };

        const tagColor = s.tagColor;
        const titleColor = s.titleColor;
        const taglineColor = s.taglineColor;
        const dividerColor = s.dividerColor;
        const currencyColor = s.currencyColor;
        const detailColor = s.detailColor;
        const checkColor = s.checkColor;
        const featureTextColor = s.featureTextColor;

        const showAmountStyle = !!item.priceCurrency;
        const isCurrencyPrefix = item.priceCurrencyPosition === 'prefix';
        const ctaLink = item.ctaLink || '#';

        // Default button style for featured/non-featured if not explicitly set
        const buttonStyle = item.buttonStyle || s.defaultButton;
        const isDarkBg = variant === 'featured-dark';
        const buttonSx = (() => {
            const base = {
                width: '100%',
                py: '14px',
                px: '20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' }
            };
            if (buttonStyle === 'outlined' || buttonStyle === 'outlined-contrast') {
                return {
                    ...base,
                    backgroundColor: 'transparent',
                    color: isDarkBg ? '#fff' : '#16121C',
                    border: `1.5px solid ${isDarkBg ? 'rgba(255,255,255,0.3)' : '#16121C'}`,
                    '&:hover': { backgroundColor: isDarkBg ? 'rgba(255,255,255,0.06)' : 'rgba(22,18,28,0.04)', boxShadow: 'none' }
                };
            }
            if (buttonStyle === 'filled-contrast') {
                return {
                    ...base,
                    backgroundColor: '#16121C',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#000', boxShadow: 'none' }
                };
            }
            return {
                ...base,
                backgroundColor: '#5C33D7',
                color: '#fff',
                '&:hover': { backgroundColor: '#4C1D95', boxShadow: 'none' }
            };
        })();

        return (
            <Box key={idx} sx={cardSx}>
                {item.eyebrow && (
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: '10px',
                            letterSpacing: '1.8px',
                            fontWeight: 700,
                            color: tagColor,
                            textTransform: 'uppercase',
                            mb: '14px'
                        }}
                        dangerouslySetInnerHTML={{ __html: item.eyebrow }}
                    />
                )}

                {item.title && (
                    <Typography
                        component="h3"
                        sx={{
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 800,
                            fontSize: { xs: '20px', md: '22px' },
                            color: titleColor,
                            lineHeight: 1.1,
                            letterSpacing: '-0.3px',
                            mb: '10px'
                        }}
                        dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                )}

                {/* Subtitle slot — always reserves min height so all cards' subtitles align */}
                <Box sx={{ minHeight: { xs: '3em', md: '3em' }, mb: '28px' }}>
                    {item.subtitle && (
                        <Typography
                            component="p"
                            sx={{
                                fontSize: '14px',
                                color: taglineColor,
                                lineHeight: 1.5,
                                m: 0
                            }}
                            dangerouslySetInnerHTML={{ __html: item.subtitle }}
                        />
                    )}
                </Box>

                {/* Price block — min height keeps all cards aligned */}
                <Box sx={{ mb: '20px', minHeight: { xs: '70px', md: '82px' } }}>
                    {showAmountStyle ? (
                        <Box sx={{
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 800,
                            fontSize: { xs: '32px', md: '38px' },
                            color: 'inherit',
                            lineHeight: 1,
                            letterSpacing: '-1px',
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '5px'
                        }}>
                            {isCurrencyPrefix && (
                                <Box component="span" sx={{
                                    fontSize: '24px',
                                    fontWeight: 500,
                                    color: currencyColor,
                                    letterSpacing: 0,
                                    lineHeight: 1
                                }}>
                                    <span dangerouslySetInnerHTML={{ __html: item.priceCurrency || '' }} />
                                </Box>
                            )}
                            <span dangerouslySetInnerHTML={{ __html: item.price || '' }} />
                            {!isCurrencyPrefix && (
                                <Box component="span" sx={{
                                    fontSize: '24px',
                                    fontWeight: 500,
                                    color: currencyColor,
                                    letterSpacing: 0,
                                    lineHeight: 1
                                }}>
                                    <span dangerouslySetInnerHTML={{ __html: item.priceCurrency || '' }} />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '20px', md: '22px' },
                            color: 'inherit',
                            lineHeight: 1.1,
                            letterSpacing: '-0.3px'
                        }}>
                            <span dangerouslySetInnerHTML={{ __html: item.price || '' }} />
                        </Box>
                    )}
                    {item.priceDetail && (
                        <Typography
                            component="p"
                            sx={{ fontSize: '13px', color: detailColor, mt: '8px' }}
                            dangerouslySetInnerHTML={{ __html: item.priceDetail }}
                        />
                    )}
                </Box>

                {item.ctaTitle && (
                    <Button
                        href={ctaLink}
                        variant="contained"
                        disableElevation
                        sx={buttonSx}
                    >
                        {item.ctaTitle}
                    </Button>
                )}

                {/* Divider between CTA and features */}
                {item.features?.length > 0 && (
                    <Box sx={{ mt: '20px', mb: '16px', borderTop: `1px solid ${dividerColor}` }} />
                )}

                {/* Features */}
                {item.features?.length > 0 && (
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, flex: 1 }}>
                        {item.features.map((feature, fidx) => (
                            <Box
                                key={fidx}
                                component="li"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    py: '9px',
                                    fontSize: '13.5px',
                                    color: featureTextColor,
                                    lineHeight: 1.5
                                }}
                            >
                                <Box sx={{ flexShrink: 0, mt: '3px' }}>
                                    <CheckIcon size={16} color={checkColor} />
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        '& strong, & b': {
                                            color: s.featureBoldColor,
                                            fontWeight: 600
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: typeof feature === 'string' ? feature : (feature?.text || '') }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    };


    return (
        <ThemeProvider theme={theme}>
            {topBanner && <CampaignBanner topBanner={topBanner} />}
            <Box sx={{
                background: (t) => t.palette.background.default,
                px: { xs: 3, md: 7.5 },
                pt: { xs: 5, md: 5 },
                pb: { xs: 8, md: 10 }
            }}>
                {/* Header */}
                {(eyebrow || sliceTitle || subtitle) && (
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                        {eyebrow && (
                            <Typography
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    mb: 1
                                }}
                                dangerouslySetInnerHTML={{ __html: eyebrow }}
                            />
                        )}
                        {eyebrow && (
                            <Box
                                aria-hidden="true"
                                sx={{
                                    height: '4px',
                                    width: 160,
                                    borderRadius: '2px',
                                    mx: 'auto',
                                    mb: { xs: 2.5, md: 3 },
                                    background: 'linear-gradient(90deg, rgba(92, 51, 215, 0) 0%, rgb(92, 51, 215) 100%)'
                                }}
                            />
                        )}
                        {sliceTitle && (
                            <Typography
                                component="h2"
                                sx={{
                                    fontFamily: 'Kanit, sans-serif',
                                    fontWeight: 900,
                                    fontSize: { xs: '40px', md: '64px' },
                                    lineHeight: 0.95,
                                    letterSpacing: '-1.5px',
                                    textTransform: 'uppercase',
                                    color: '#16121C',
                                    mb: '22px'
                                }}
                                dangerouslySetInnerHTML={{ __html: sliceTitle }}
                            />
                        )}
                        {subtitle && (
                            <Typography
                                component="p"
                                sx={{
                                    fontSize: '17px',
                                    color: '#4A4744',
                                    maxWidth: '520px',
                                    lineHeight: 1.5,
                                    mx: 'auto'
                                }}
                                dangerouslySetInnerHTML={{ __html: subtitle }}
                            />
                        )}
                    </Box>
                )}

                {/* Plans grid */}
                {(() => {
                    const gridColumns = {
                        xs: '1fr',
                        md: items.length === 1 ? '1fr' : (items.length >= 3 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)')
                    };
                    const gridMaxWidth = items.length >= 3 ? '1100px' : '860px';
                    const containerMaxWidth = items.length >= 3 ? '1128px' : '888px';
                    const hasAnyNote = items.some(i => i.footerNote);
                    return (
                        <>
                            <Box sx={{
                                background: 'rgba(22, 18, 28, 0.05)',
                                borderRadius: { xs: '16px', md: '20px' },
                                p: { xs: '12px', md: '14px' },
                                maxWidth: containerMaxWidth,
                                mx: 'auto'
                            }}>
                                {/* Cards row — all cards stretch to equal height */}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: gridColumns,
                                    gap: '14px',
                                    alignItems: 'stretch'
                                }}>
                                    {items.map((item, idx) => planFor(item, idx))}
                                </Box>
                            </Box>

                            {/* Per-plan notes row — sits below the container box */}
                            {hasAnyNote && (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: gridColumns,
                                    gap: '14px',
                                    maxWidth: gridMaxWidth,
                                    mx: 'auto',
                                    mt: { xs: '16px', md: '20px' },
                                    px: { xs: '12px', md: '14px' }
                                }}>
                                    {items.map((item, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                textAlign: 'center',
                                                fontSize: '13px',
                                                color: '#78746E',
                                                px: 2,
                                                '& a': {
                                                    color: '#5C33D7',
                                                    fontWeight: 600,
                                                    textDecoration: 'none',
                                                    borderBottom: '1px solid rgba(92, 51, 215, 0.3)'
                                                },
                                                '& a:hover': { borderBottomColor: '#5C33D7' }
                                            }}
                                            {...(item.footerNote ? { dangerouslySetInnerHTML: { __html: item.footerNote } } : {})}
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    );
                })()}

                {/* Currency switcher — only when >1 currency configured server-side. Placed
                    below the cards so it doesn't push the main pricing content down. Dropdown
                    opens upward to avoid collision with the help note below. */}
                {currencies.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, md: 4 } }}>
                        <Box
                            ref={currencyMenuRef}
                            sx={{ position: 'relative' }}
                        >
                            <Button
                                onClick={() => setCurrencyMenuOpen((o) => !o)}
                                aria-expanded={currencyMenuOpen}
                                aria-haspopup="listbox"
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.85rem',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '999px',
                                    px: 2,
                                    py: 0.5,
                                    minWidth: 0,
                                    '&:hover': { background: 'rgba(0,0,0,0.04)' }
                                }}
                            >
                                {currentCurrency}
                                <Box component="span" sx={{ ml: 0.75, fontSize: '0.7rem', opacity: 0.6 }}>▴</Box>
                            </Button>
                            {currencyMenuOpen && (
                                <Box
                                    role="listbox"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 'calc(100% + 4px)',
                                        left: 0,
                                        right: 0,
                                        background: '#fff',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '8px',
                                        boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                                        overflow: 'hidden',
                                        zIndex: 10
                                    }}
                                >
                                    {currencies.filter((c) => c !== currentCurrency).map((cur) => (
                                        <Box
                                            key={cur}
                                            role="option"
                                            onClick={() => handleCurrencyChange(cur)}
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                fontSize: '0.85rem',
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                                '&:hover': { background: 'rgba(0,0,0,0.04)' }
                                            }}
                                        >
                                            {cur}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Help note box below cards (icon + title + body) */}
                {(helpTitle || helpBody) && (
                    <Box sx={{
                        maxWidth: '860px',
                        mx: 'auto',
                        mt: { xs: 5, md: 7 },
                        p: { xs: '24px', md: '32px 36px' },
                        background: '#FBF9F7',
                        border: '0.5px solid #E8E4DF',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: '16px', md: '24px' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'rgba(92, 51, 215, 0.08)',
                            color: '#5C33D7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <HelpIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {helpTitle && (
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontFamily: 'Kanit, sans-serif',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        color: '#16121C',
                                        m: 0,
                                        mb: helpBody ? '4px' : 0
                                    }}
                                    dangerouslySetInnerHTML={{ __html: helpTitle }}
                                />
                            )}
                            {helpBody && (
                                <Typography
                                    component="p"
                                    sx={{
                                        fontSize: '13.5px',
                                        color: '#4A4744',
                                        lineHeight: 1.5,
                                        m: 0,
                                        '& a': {
                                            color: '#5C33D7',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            borderBottom: '1px solid #5C33D7'
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: helpBody }}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default PricingPlanSlice;
